import { useRef } from 'react'

import { useEventListener, useMemoizedFn } from 'ahooks'

import {
  UseResizerDragDirection,
  UseResizerDragPosition,
  UseResizerDragProps
} from './interface'

const initialPosition = {
  x: 0,
  y: 0
}

export const useResizerDrag = ({
  rootElement,
  rightDragbar,
  bottomDragbar,
  onDragStart,
  onDraging,
  onDragEnd
}: UseResizerDragProps) => {
  const mouseStartPositionRef = useRef<UseResizerDragPosition>(initialPosition)
  const dragDirectionRef = useRef<UseResizerDragDirection>(undefined)
  // 是否正在拖拽 dragbar
  const isDragingRef = useRef(false)

  // 开始拖拽
  const handleMouseDown = (
    event: MouseEvent,
    direction: UseResizerDragDirection
  ) => {
    const position = {
      x: event.clientX,
      y: event.clientY
    }
    isDragingRef.current = true
    dragDirectionRef.current = direction
    mouseStartPositionRef.current = position
    onDragStart({
      mouseStartPosition: position,
      mouseCurrentPosition: position,
      dragDirection: direction
    })
  }

  // 拖拽中
  const handleMouseMove = useMemoizedFn((event: MouseEvent) => {
    // 只处理 dragbar 发起的拖拽事件
    if (!isDragingRef.current) {
      return
    }
    // 如果设置了根容器，且当前事件的目标不在根容器内，则不处理
    if (
      rootElement?.current &&
      !rootElement.current.contains(event.target as Node)
    ) {
      return
    }
    onDraging({
      mouseStartPosition: mouseStartPositionRef.current,
      mouseCurrentPosition: {
        x: event.clientX,
        y: event.clientY
      },
      dragDirection: dragDirectionRef.current
    })
  })

  // 拖拽结束
  const handleMouseUp = useMemoizedFn((event: MouseEvent) => {
    // 只处理 dragbar 发起的拖拽事件
    if (!isDragingRef.current) {
      return
    }
    isDragingRef.current = false
    onDragEnd({
      mouseStartPosition: mouseStartPositionRef.current,
      mouseCurrentPosition: {
        x: event.clientX,
        y: event.clientY
      },
      dragDirection: dragDirectionRef.current
    })
  })

  /* ****************************** 右边边框的 dragbar ***************************** */
  useEventListener(
    'mousedown',
    event => {
      handleMouseDown(event, 'right')
    },
    {
      target: rightDragbar,
      passive: true
    }
  )
  useEventListener('mousemove', handleMouseMove, {
    target: rightDragbar,
    passive: true
  })

  useEventListener('mouseup', handleMouseUp, {
    target: rightDragbar,
    passive: true
  })

  /* ****************************** 下边边框的 dragbar ***************************** */
  useEventListener(
    'mousedown',
    event => {
      handleMouseDown(event, 'bottom')
    },
    {
      target: bottomDragbar,
      passive: true
    }
  )
  useEventListener('mousemove', handleMouseMove, {
    target: bottomDragbar,
    passive: true
  })

  useEventListener('mouseup', handleMouseUp, {
    target: bottomDragbar,
    passive: true
  })

  /* *********************************** 根容器 ********************************** */
  useEventListener('mousemove', handleMouseMove, {
    target: rootElement,
    passive: true
  })
  useEventListener('mouseup', handleMouseUp, {
    target: rootElement,
    passive: true
  })
}
